import { ChangeEvent, useEffect, useState } from "react";
import { Dropdown } from "./Forms/Inputs";
import { Dispatch, SetStateAction } from "react";
import _ from "lodash";
import { useAtomValue } from "jotai";
import { modelDataAtom } from "../atoms";
import { checkFieldName } from "../utils";

interface ISortState {
  field: string;
  order: string;
}

interface ISortMenuProps {
  fields: string[];
  modifySortState: Dispatch<SetStateAction<ISortState>>;
  sortState: ISortState;
  modelName: string;
}

const orderOptions = [
  { label: "Ascending", value: "ASC" },
  { label: "Descending", value: "DESC" },
];

// Default sorting fields
// ----------------------
// Can be modified for the global project
export const defaultSortFields = ["sort_id", "name", "title", "date"];

// Initial Sort State
export const useSortState = (initField: string) =>
  useState<ISortState>({
    field: initField,
    order: "ASC",
  });

// Sort Menu
export const SortMenu = ({
  modelName,
  fields,
  modifySortState,
  sortState,
}: ISortMenuProps) => {
  const modelData = useAtomValue(modelDataAtom);
  const [availableFields, setAvailableFields] = useState<string[]>(fields);

  useEffect(() => {
    const model = modelData.find((m) => m.name === modelName);
    if (!model) {
      setAvailableFields([]);
    } else {
      const matchedFields = fields.reduce((arr: string[], field: string) => {
        if (_.has(model.fields, field)) return [...arr, field];
        return arr;
      }, []);
      setAvailableFields(matchedFields);
      modifySortState({ field: matchedFields[0], order: "ASC" });
    }
  }, [modelName]);

  const optionsFromFields = availableFields.map((field) => {
    const [label] = checkFieldName(field);
    return {
      label,
      value: field,
    };
  });

  const changeFieldValue = (value: string) => {
    modifySortState({
      ...sortState,
      field: value,
    });
  };

  const changeOrderValue = (value: string) => {
    modifySortState({
      ...sortState,
      order: value,
    });
  };

  return (
    <div className="flex gap-4 pb-4">
      <div className="text-grayText flex flex-col text-sm font-medium gap-1">
        Sort by
        <Dropdown
          name="field"
          type="string"
          value={sortState.field}
          options={optionsFromFields}
          onChange={(e) => changeFieldValue(e.target.value)}
          className="p-1.5 min-w-[16rem]"
        />
      </div>
      <div className="text-grayText flex flex-col text-sm font-medium gap-1">
        Order
        <Dropdown
          name="order"
          type="boolean"
          value={sortState.order}
          options={orderOptions}
          onChange={(e) => changeOrderValue(e.target.value)}
          className="p-1.5 min-w-[16rem]"
        />
      </div>
    </div>
  );
};
